// api.ts — typed endpoint groups for the thirdeye server.
//
// Auth is cookie-driven: the OIDC callback at /api/oidc/callback sets a
// `thirdeye_auth` HttpOnly cookie which axios's `withCredentials: true`
// returns on every request. We also keep the lib/web apiClient around so
// `useAuth` from common can drive identity state via /api/me and so a
// pasted JWT in localStorage still works for headless callers.
import axios from 'axios';
import { createApiClient } from 'common';
import type {
  GpuMetricsResponse, GpuHistoryEntry, MetricsOverview,
  MCPServer,
} from './types';

export const TOKEN_KEY = 'thirdeye_token';
export const apiClient = createApiClient({ tokenKey: TOKEN_KEY });

const api = axios.create({
  baseURL: '/api',
  withCredentials: true,
});

// If a JWT happens to be in localStorage (paste-from-CLI flow), attach it
// as a Bearer header. The cookie set by /api/oidc/callback authenticates
// browser sessions without this code path firing.
api.interceptors.request.use((cfg) => {
  const t = apiClient.getToken();
  if (t) {
    cfg.headers = cfg.headers || {};
    (cfg.headers as Record<string, string>).Authorization = `Bearer ${t}`;
  }
  return cfg;
});

// 401 → clear whatever stale token is in localStorage and route the user
// to /api/oidc/login so the OIDC handler can re-establish the cookie. We
// never auto-reload on 401 without a token; that's the pre-login state.
api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err?.response?.status === 401) {
      apiClient.clearToken();
      if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/api/oidc')) {
        window.location.replace('/api/oidc/login?return_to=' + encodeURIComponent(window.location.pathname));
      }
    }
    return Promise.reject(err);
  },
);

export const metricsApi = {
  getGpu: () => api.get<GpuMetricsResponse>('/gpu').then(res => res.data),
  getGpuHistory: (hours = 24) => api.get<GpuHistoryEntry[]>(`/gpu/history?hours=${hours}`).then(res => res.data),
  getOverview: () => api.get<MetricsOverview>('/metrics/overview').then(res => res.data),
};

// MCP-server admin endpoints. The deployed manager registers these as
// `/api/mcp-servers` (dashed); the URL is matched here exactly to
// avoid hitting the SPA's index.html catch-all, which would return
// HTML and crash the always-mounted MCP modal's `.map(...)` on a
// non-array. ensureArray below is the second line of defence so a
// future route rename surfaces as "no servers" rather than a page
// crash.
function ensureArray<T>(v: unknown): T[] {
  return Array.isArray(v) ? (v as T[]) : [];
}

export const mcpApi = {
  list: () => api.get<MCPServer[]>('/mcp-servers').then(res => ensureArray<MCPServer>(res.data)),
  get: (id: string) => api.get<MCPServer>(`/mcp-servers/${id}`).then(res => res.data),
  save: (server: MCPServer) => api.post('/mcp-servers', server).then(res => res.data),
  delete: (id: string) => api.delete(`/mcp-servers/${id}`).then(res => res.data),
  discover: (image: string, runtimeArgs?: string[]) => api.post<{ image: string, tools: any[], raw_help: string }>('/mcp-discover', { image, runtime_args: runtimeArgs }).then(res => res.data),
};

// Memories the harness keeps for the signed-in user, filed by scope
// (harness/src/memory/store.ts): the Dreams page lists, edits, deletes.
export type MemoryScope = 'user' | 'repo' | 'session';

export interface MemoryDoc {
  slug: string;
  name: string;
  description: string;
  kind: string;
  body: string;
  confidence: number;
  tags: string[];
  createdAt: string;
  updatedAt: string;
}

export interface MemoryRow {
  scope: MemoryScope;
  /** With scope and slug, identifies the memory. Opaque. */
  key: string;
  repo?: string;
  session_id?: string;
  session_name?: string;
  memory: MemoryDoc;
  updated_at: string;
}

export interface MemoryEdit {
  name: string;
  description: string;
  body: string;
}

const memoryRef = (m: MemoryRow) => ({ scope: m.scope, key: m.key, slug: m.memory.slug });

export const memoriesApi = {
  list: () => api.get<MemoryRow[]>('/memories').then(res => ensureArray<MemoryRow>(res.data)),
  update: (m: MemoryRow, edit: MemoryEdit) =>
    api.patch('/memories', { ...memoryRef(m), ...edit }).then(res => res.data),
  delete: (m: MemoryRow) => api.delete('/memories', { data: memoryRef(m) }).then(res => res.data),
};

// Per-user MCP server enable-set. Persisted in postgres alongside
// sessions; chat turns aggregate tools from these servers.
export const mcpPrefsApi = {
  get: () => api.get<{ enabled_server_ids: string[] }>('/me/mcp-prefs').then(res => ensureArray<string>(res.data?.enabled_server_ids)),
  set: (enabledServerIDs: string[]) =>
    api.put<{ enabled_server_ids: string[] }>('/me/mcp-prefs', { enabled_server_ids: enabledServerIDs })
      .then(res => ensureArray<string>(res.data?.enabled_server_ids)),
};

// InferenceStats mirrors the shape inference's stats.Snapshot returns —
// scraped per-pod from vLLM /metrics (tok/s) and nvidia-smi (power).
export interface InferencePodStat {
  name: string;
  phase: string;
  node_name?: string;
  restart_count: number;
  reason?: string;
  message?: string;
  power_watts: number;
  tokens_per_sec: number;
  generation_tokens_total: number;
}

export interface InferenceModelStat {
  name: string;
  replicas: number;
  ready: number;
  pods: InferencePodStat[];
  tokens_per_sec: number;
  generation_tokens_total: number;
  num_requests_running: number;
  num_requests_waiting: number;
}

export interface InferenceStats {
  gpu: {
    total_capacity: number;
    in_use: number;
    power_watts: number;
    tokens_per_sec: number;
    utilization_percent: number;
    gpus_sampled: number;
  };
  nodes: Array<{ name: string; schedulable: boolean; gpus: number; ready: boolean }>;
  models: InferenceModelStat[];
}

export const inferenceApi = {
  stats: () => api.get<InferenceStats>('/inference/stats').then(res => res.data),
};

// ─── Sessions ───────────────────────────────────────────────────────────
//
// Sessions are user-owned chat threads against the inference fleet. The
// `streamMessage` helper opens an SSE connection — the browser doesn't
// re-fetch the session at the end because the server's delta frames
// already contain everything the assistant bubble needs to render.

export interface SessionMessage {
  role: 'user' | 'assistant' | 'system';
  content: string;
  // Qwen3-style thinking trace surfaced from delta.reasoning_content for
  // assistant turns. Empty for user/system messages and non-reasoning models.
  reasoning?: string;
  // True when the text is the harness reporting a failure (upstream 400,
  // iteration cap) rather than the model's own reply. Set by the session
  // Pod on /api/history entries, and locally when an `error` SSE frame
  // lands mid-turn. Sessions.tsx renders these as errors, not replies.
  error?: boolean;
  // Tool-UI markdown, when the tool's MCP server opted into the
  // `thirdeye.lab0.us/tool-ui` capability. Rendered by LiveMarkdown with
  // Axios components; the model never sees it. Arrives live on a
  // `tool_ui` SSE frame and is restored from /api/history on reload.
  tool_ui?: string;
  tool_call_id?: string;
  // Set on entries that record a tool call rather than something anyone
  // said. Rendered as a collapsed evidence card so every MCP call leaves
  // a trace, even one that drew no UI of its own.
  tool_name?: string;
  tool_input?: unknown;
  tool_output?: string;
  tool_error?: boolean;
  // True between tool_started and tool_completed.
  tool_running?: boolean;
  timestamp: string;
}

export interface Session {
  id: string;
  name: string;
  // subtitle is a server-regenerated one-line status shown under name in
  // the sidebar. After every assistant turn the session Pod posts the
  // tail of the conversation to the manager, which refreshes both fields
  // with a single completion against the fleet's cheapest model: the
  // subtitle summarizes that tail, the name summarizes the accumulated
  // subtitles. Both default to "" until the first turn lands; the UI
  // renders fallbacks in that window.
  subtitle: string;
  // name_user_set pins the name once the user has edited it in the
  // sidebar so subsequent turns don't clobber the operator's label.
  // Subtitle keeps updating either way. sessionsApi.autoTitle clears it.
  name_user_set: boolean;
  owner_email: string;
  model: string;
  messages: SessionMessage[];
  created_at: string;
  updated_at: string;
  // The workspace this session runs in: the Pod its commands execute on
  // and the working tree its file tools see, shared with every other
  // session in it. Fixed at creation.
  workspace_id: string;
  // When this conversation last said something — the newest message in
  // its journal, stamped server-side. The sidebar sorts on it and prints
  // it under each subtitle. Absent until the session's first turn, which
  // is what `sessionRecency` in chatState.ts falls back for; not the same
  // as `updated_at`, which also moves on a rename.
  last_message_at?: string;
  // Fork lineage, fixed at creation: the session this one was forked from
  // and the parent's event seq it branched at. Absent on a root session.
  // The sidebar nests forks under their parent from these.
  parent_session_id?: string;
  fork_seq?: number;
}

// Workspace is a session Pod and its working tree — the machine several
// sessions can share. Lifecycle (suspend/resume) and Code OSS belong to
// it, not to any one session.
export interface Workspace {
  id: string;
  name: string;
  owner_email: string;
  // The KateProcess behind it; empty when suspended or never provisioned.
  kate_process_name?: string;
  // "" / undefined: active, with readiness derived live from the Pod.
  // "suspended": the Pod was torn down, the volumes kept.
  phase?: '' | 'suspended';
  last_active?: string;
  created_at: string;
  updated_at: string;
  // Where the working tree lives. "garage": a bucket the harness mounts
  // itself, so the conversation and file tools need no Pod. "" / absent:
  // the legacy per-workspace volume, which only the Pod can reach.
  storage?: '' | 'garage';
}

// SessionCost is what one conversation has cost to run, from
// GET /api/sessions/{id}/cost.
//
// Local turns are priced from GPU watt-hours the inference fleet
// actually measured while they ran; turns proxied to an external
// provider are priced from that provider's rate card and counted in
// provider_cost_usd (a subset of cost_usd).
export interface SessionCost {
  // False means the cost is unknown — metering off, records aged out of
  // inference's in-memory ring, or inference unreachable. Render nothing
  // rather than $0.00, which would read as "this was free".
  available: boolean;
  reason?: string;
  cost_usd?: number;
  provider_cost_usd?: number;
  energy_wh?: number;
  requests?: number;
  prompt_tokens?: number;
  completion_tokens?: number;
  // True when some turns went unmetered, so cost_usd is a floor. The UI
  // prefixes the figure with "≥".
  partial?: boolean;
  // True when inference could not be read just now, so the stored figure
  // may be missing the last few moments' requests.
  stale?: boolean;
  // The individual requests, oldest first. Cost is shown only as the
  // session total in the header (SessionCostBadge).
  entries?: SessionCostEntry[];
}

// SessionCostEntry is one inference request the session caused — a turn's
// completion, a subagent's, or the title call after a reply.
export interface SessionCostEntry {
  started_at: string;
  ended_at: string;
  model: string;
  cost_usd: number;
  energy_wh: number;
  prompt_tokens: number;
  completion_tokens: number;
  // "energy" | "provider" | "unmetered"
  cost_source: string;
}

// SessionPhaseLive is the live lifecycle phase the SSE /events
// endpoint streams. Distinct from Workspace.phase because the persisted
// store value only distinguishes active vs suspended — provisioning
// and ready are derived from live kubelet state.
export type SessionPhaseLive =
  | 'provisioning'
  | 'ready'
  | 'suspended';

export interface SessionEventsHandle {
  /** Cancel the SSE connection (idempotent). */
  close(): void;
}

export interface SessionEventsHandlers {
  onPhase: (phase: SessionPhaseLive) => void;
  onLog?: (line: string) => void;
  onError?: (msg: string) => void;
  onClose?: () => void;
}

// modelsApi exposes the inference fleet's served model ids for the
// new-session model picker. `default` is the id the manager falls back to
// when a session pins none; it's also guaranteed to be first in `models`.
export const modelsApi = {
  list: () =>
    api
      .get<{ models: string[]; default: string }>('/models')
      .then((res) => res.data ?? { models: [], default: '' }),
};

export const workspacesApi = {
  list: () => api.get<Workspace[]>('/workspaces').then((res) => res.data ?? []),
  rename: (id: string, name: string) =>
    api.patch<Workspace>(`/workspaces/${id}/name`, { name }).then((res) => res.data),
  // delete tears down the Pod, reaps the working tree and installed
  // packages, and deletes every session in the workspace.
  delete: (id: string) => api.delete(`/workspaces/${id}`).then((res) => res.data),
  // suspend tears down the Pod and keeps everything else; every session
  // in the workspace goes with it, since they share the Pod.
  suspend: (id: string) =>
    api.post<Workspace>(`/workspaces/${id}/suspend`).then((res) => res.data),
  // resume brings the Pod back. Returns immediately — follow /events for
  // the boot logs and Ready transition.
  resume: (id: string) =>
    api.post<Workspace>(`/workspaces/${id}/resume`).then((res) => res.data),
  // codeLaunch wakes the workspace's Pod if it needs it and returns a
  // one-time URL on the code host for an iframe (or a new tab) to open
  // Code OSS with, in `theme`: the page's light/dark scheme, so the editor
  // matches it. The editor never runs on this origin; see CodeOssPane.
  codeLaunch: (id: string, theme: 'light' | 'dark') =>
    api
      .post<{ url: string; origin: string }>(`/workspaces/${id}/code/launch`, null, { params: { theme } })
      .then((res) => res.data),
};

export const sessionsApi = {
  list: () => api.get<Session[]>('/sessions').then((res) => res.data ?? []),
  // create starts a session in `workspaceId`, or in a new workspace of
  // its own (named `workspaceName`) when none is given.
  create: (name: string, model?: string, opts: { workspaceId?: string; workspaceName?: string } = {}) =>
    api
      .post<Session>('/sessions', {
        name,
        model,
        workspace_id: opts.workspaceId ?? '',
        workspace_name: opts.workspaceName ?? '',
      })
      .then((res) => res.data),
  get: (id: string) => api.get<Session>(`/sessions/${id}`).then((res) => res.data),
  delete: (id: string) => api.delete(`/sessions/${id}`).then((res) => res.data),
  // open tells the manager the user opened this session, which is what
  // starts its workspace's Pod. Answers 202 at once with the workspace;
  // the Pod comes up in the background (follow /events for the boot log).
  open: (id: string) =>
    api.post<Workspace>(`/sessions/${id}/open`).then((res) => res.data),

  // rename overrides the auto-generated session title and pins
  // `name_user_set=true` on the server, which is what stops the
  // turn-end regen from overwriting what the user typed.
  rename: (id: string, name: string) =>
    api.patch<Session>(`/sessions/${id}/name`, { name }).then((res) => res.data),

  // autoTitle clears that pin and hands the name back to the turn-end
  // regen. The way out of a rename — without it, one edit disables
  // auto-titling on that session for good.
  autoTitle: (id: string) =>
    api.patch<Session>(`/sessions/${id}/name`, { auto: true }).then((res) => res.data),

  // setModel repoints an existing session at a different model. This
  // only persists the choice — the running Pod adopts it from the
  // `model` field streamMessage puts on the chat request. Persisting is
  // what makes the switch outlive the Pod: provisionSessionPod bakes
  // LLM_MODEL into the Pod env, so an unstored choice would revert on
  // the next suspend/resume or creds-refresh restart.
  setModel: (id: string, model: string) =>
    api.patch<Session>(`/sessions/${id}/model`, { model }).then((res) => res.data),

  // cost is what this conversation has spent on the inference fleet,
  // measured from GPU energy rather than quoted from a token price list.
  // Always resolves — an unreadable cost comes back as
  // `{ available: false, reason }` so a metering outage can't make a
  // working conversation render as broken.
  cost: (id: string) =>
    api.get<SessionCost>(`/sessions/${id}/cost`).then(
      (res) => res.data ?? { available: false },
    ),

  // streamMessage POSTs the user's content to the per-session Pod and
  // parses the SSE response line-by-line. fetch() rather than
  // EventSource because EventSource is GET-only.
  //
  // The URL resolves through `resolveChatUrl(session)`, the manager's
  // reverse proxy to thirdeye-harness.
  // `model` rides along on the turn rather than being switched in a
  // separate call: the Pod applies it inside the turn lock, so there is
  // no window where the message goes out under the model the user just
  // switched away from. Sending it on every turn (not just after a
  // change) also re-syncs a Pod whose env lags the stored choice.
  streamMessage: (
    session: Session,
    content: string,
    handlers: StreamHandlers,
    model?: string,
    // Resolves true when the turn streamed to completion. Callers that
    // only need "did the transport work" can ignore it and rely on the
    // rejection instead.
  ): Promise<boolean> =>
    consumeSessionStream(resolveChatUrl(session, '/api/chat'), handlers, {
      method: 'POST',
      body: JSON.stringify(model ? { content, model } : { content }),
    }),

  // startTurn asks the Pod to begin a turn and returns as soon as it
  // has. The reply arrives on the session's socket (see
  // components/live/sessionLive.ts), not on this response.
  //
  // The difference from streamMessage is who owns the turn. Here the Pod
  // does: the turn outlives the request, so it survives a reload, and
  // every watcher sees it — the sidebar, a second tab, the CLI. With
  // streamMessage the turn was the response, and closing the response
  // was the only thing keeping it alive.
  //
  // Rejects on 409, which means a turn is already running and the caller
  // should have steered instead.
  startTurn: async (
    session: Session,
    content: string,
    model?: string,
  ): Promise<{ turn_id: string }> => {
    const res = await fetch(resolveChatUrl(session, '/api/turns'), {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', ...chatAuthHeaders() },
      body: JSON.stringify(model ? { content, model } : { content }),
    });
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(text || `HTTP ${res.status}`);
    }
    return res.json();
  },

  // steer adds a message to the turn already running, so the composer
  // stays usable while the model works. The Pod appends it as a user
  // turn before the next model round-trip — the model reads it as part
  // of the conversation rather than as an interruption.
  //
  // Resolves false when nothing was running, which is an ordinary race
  // (the turn ended between pressing send and this landing) and means
  // the caller should send the message as a new turn instead. A
  // transport failure rejects, so "the message did not land" and "there
  // was nothing to land in" stay distinguishable.
  steer: async (session: Session, content: string): Promise<boolean> => {
    const res = await fetch(resolveChatUrl(session, '/api/steer'), {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', ...chatAuthHeaders() },
      body: JSON.stringify({ content }),
    });
    if (!res.ok) throw new Error(`steer: HTTP ${res.status}`);
    const body = await res.json();
    return Boolean(body?.steered);
  },

  // history reads a session's conversation, from whichever copy can
  // answer.
  //
  // The harness first, always: it keeps every session's conversation
  // whether or not the workspace's Pod is running, and the manager's
  // chat proxy passes a history read straight through without waiting
  // for a Pod. Gating this on the Pod's phase is what used to blank the
  // chat for any session whose Pod was asleep, booting, or whose phase
  // had not loaded yet: the fallback below is empty for every session
  // since the harness took the conversation over.
  //
  // A harness that answers with an empty conversation is not taken at
  // its word; the manager's journal decides between "nothing said yet"
  // and a conversation the harness came back without.
  //
  // Both paths fold to [] rather than throwing. An unreadable
  // transcript should leave the chat shell standing.
  history: async (session: Session): Promise<SessionMessage[]> => {
    try {
      const res = await fetch(resolveChatUrl(session, '/api/history'), {
        method: 'GET',
        credentials: 'include',
        headers: chatAuthHeaders(),
      });
      if (res.ok) {
        const live = (await res.json()) as SessionMessage[];
        if (live.length > 0) return live;
      }
    } catch {
      // Fall through to the journal.
    }
    return sessionsApi.transcript(session.id);
  },

  // transcript is the manager's copy, folded out of the session
  // journal. Serves a session with no Pod to ask, and is the read that
  // makes suspend non-destructive from the user's side rather than only
  // on paper.
  transcript: async (id: string): Promise<SessionMessage[]> => {
    try {
      const res = await api.get<{ messages?: SessionTranscriptEntry[] }>(
        `/sessions/${id}/transcript`,
      );
      return (res.data?.messages ?? []).flatMap(entryToMessages);
    } catch {
      return [];
    }
  },

  // fork branches this conversation into a new session in the same
  // workspace, sharing everything said so far. Made by the harness, which
  // holds the conversation: the manager's own fork endpoint only branches
  // sessions from before the harness moved. Resolves to the new session's
  // id and name.
  fork: async (id: string, name: string): Promise<{ id: string; name: string }> => {
    const res = await fetch(`/api/sessions/${encodeURIComponent(id)}/chat/api/fork`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', ...chatAuthHeaders() },
      body: JSON.stringify({ name }),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(body?.error ?? `fork: HTTP ${res.status}`);
    return body as { id: string; name: string };
  },
};

// One entry of GET /api/sessions/{id}/transcript — a conversation from
// before the harness moved, whose messages the manager stores and returns
// without looking inside. Sessions since then are read from the harness,
// which returns SessionMessage directly; the shaping below is what makes
// the two render the same.
interface SessionTranscriptEntry {
  seq: number;
  turn_id?: string;
  at?: string;
  message?: HarnessMessage;
}

interface HarnessMessage {
  role: 'system' | 'user' | 'assistant' | 'tool';
  content?: string;
  reasoning?: string;
  tool_calls?: { id: string; name: string; arguments?: string }[];
  tool_call_id?: string;
  is_error?: boolean;
  error?: boolean;
  tool_ui?: string;
  timestamp?: string;
}

// Names the tool each call id ran, so a `tool` message — which records
// only its result — can be labelled with what produced it.
const toolNames = new WeakMap<SessionTranscriptEntry[], Map<string, string>>();

// entryToMessages mirrors the Pod's app/history.py so a conversation
// reads identically whichever copy answered. Two shaping rules, both
// about what the chat pane can render:
//
//   - a `tool` message becomes an otherwise-empty assistant entry
//     carrying the tool's name and whether it failed, plus its tool_ui.
//     Replaying raw tool output would bury the conversation in build
//     logs; dropping it outright leaves an assistant that apparently
//     knew things for no reason.
//   - turns that flatten to empty text are dropped, so a turn that was
//     purely tool calls leaves no blank bubble.
function entryToMessages(
  entry: SessionTranscriptEntry,
  _i: number,
  all: SessionTranscriptEntry[],
): SessionMessage[] {
  const msg = entry.message;
  if (!msg) return [];

  let names = toolNames.get(all);
  if (!names) {
    names = new Map<string, string>();
    for (const e of all) {
      for (const call of e.message?.tool_calls ?? []) names.set(call.id, call.name);
    }
    toolNames.set(all, names);
  }

  const timestamp = msg.timestamp || entry.at || '';
  if (msg.role === 'tool') {
    return [
      {
        role: 'assistant',
        content: '',
        tool_ui: msg.tool_ui,
        tool_call_id: msg.tool_call_id,
        tool_name: names.get(msg.tool_call_id ?? '') || 'tool',
        tool_error: msg.is_error,
        timestamp,
      },
    ];
  }
  if (msg.role !== 'user' && msg.role !== 'assistant' && msg.role !== 'system') {
    return [];
  }
  const content = (msg.content ?? '').trim();
  if (!content) return [];
  return [
    {
      role: msg.role,
      content,
      reasoning: (msg.reasoning ?? '').trim(),
      error: msg.error,
      timestamp,
    },
  ];
}

// resolveChatUrl decides where chat traffic goes. There is one answer
// now: `/api/sessions/<id>/chat<path>`, which the manager reverse-proxies
// to thirdeye-harness.
//
// The alternative used to be Pod-direct, over a per-session subdomain, on
// the theory that the Pod served the chat. It does not any more — the
// agent runs in thirdeye-harness and the Pod is an sshd (HARNESS.md) — so
// the subdomain path is not merely unprovisioned, it addresses a server
// that no longer exists, and the route probe that chose between the two
// is gone with it.
function resolveChatUrl(session: Session, path: string): string {
  return `/api/sessions/${session.id}/chat${path}`;
}

function chatAuthHeaders(): Record<string, string> {
  const token = apiClient.getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}


// Internal SSE consumer shared by streamMessage + streamTail. Returns
// true if the server opened an SSE stream, false on 204 No Content
// (used by streamTail to signal "no in-flight gen"). Errors from the
// stream itself are reported via handlers.onError.
type StreamHandlers = {
  onDelta: (text: string) => void;
  onThinking?: (text: string) => void;
  // A tool attached a UI to its call. Carries the *initial* markdown only
  // — every later replacement arrives on one of the UI's own channel
  // sockets, so there is no second SSE frame to handle.
  onToolUi?: (toolCallId: string, markdown: string) => void;
  // Tool activity, so the pane can show what the model actually did
  // rather than jumping from "thinking" straight to an answer.
  onToolStarted?: (toolCallId: string, name: string, input: unknown) => void;
  onToolCompleted?: (toolCallId: string, output: string, isError: boolean) => void;
  onDone: () => void;
  onError: (msg: string) => void;
};

// subscribeSessionEvents opens an SSE connection to
// /api/sessions/{id}/events and dispatches lifecycle frames to the
// supplied handlers. Returns a handle whose close() cancels the
// fetch (idempotent). Used by Sessions.tsx to gate the chat input
// while the per-session pod is provisioning and to surface kubelet/
// uvicorn boot output in the empty chat pane.
//
// Wire format mirrors consumeSessionStream below: one event per
// blank-line-separated frame, `event:`/`data:` fields. The endpoint
// emits these names: `phase` (provisioning|ready|suspended) and
// `log` (one stdout line from the sessionenv pod).
export function subscribeSessionEvents(
  sessionId: string,
  handlers: SessionEventsHandlers,
): SessionEventsHandle {
  const ctrl = new AbortController();
  const token = apiClient.getToken();
  const headers: Record<string, string> = { Accept: 'text/event-stream' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  (async () => {
    try {
      const res = await fetch(`/api/sessions/${sessionId}/events`, {
        method: 'GET',
        credentials: 'include',
        headers,
        signal: ctrl.signal,
      });
      if (!res.ok || !res.body) {
        const text = await res.text().catch(() => '');
        handlers.onError?.(text || `HTTP ${res.status}`);
        handlers.onClose?.();
        return;
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = '';
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        let idx: number;
        while ((idx = buf.indexOf('\n\n')) !== -1) {
          const frame = buf.slice(0, idx);
          buf = buf.slice(idx + 2);
          let event = 'message';
          let data = '';
          for (const line of frame.split('\n')) {
            if (line.startsWith('event:')) event = line.slice(6).trim();
            else if (line.startsWith('data:'))
              data += line.startsWith('data: ') ? line.slice(6) : line.slice(5);
          }
          if (event === 'phase') handlers.onPhase(data as SessionPhaseLive);
          else if (event === 'log') handlers.onLog?.(data);
        }
      }
    } catch (err) {
      // AbortError on close() is the happy path; everything else is
      // a real error worth surfacing.
      if (!(err instanceof DOMException && err.name === 'AbortError')) {
        handlers.onError?.(`events stream: ${err}`);
      }
    } finally {
      handlers.onClose?.();
    }
  })();
  return { close: () => ctrl.abort() };
}

async function consumeSessionStream(
  url: string,
  handlers: StreamHandlers,
  init: { method: 'GET' | 'POST'; body?: string },
): Promise<boolean> {
  const {
    onDelta, onThinking, onToolUi, onToolStarted, onToolCompleted, onDone, onError,
  } = handlers;
  const token = apiClient.getToken();
  const headers: Record<string, string> = {
    'Accept': 'text/event-stream',
  };
  if (init.body !== undefined) headers['Content-Type'] = 'application/json';
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(url, {
    method: init.method,
    credentials: 'include',
    headers,
    body: init.body,
  });
  if (res.status === 204) return false;
  if (!res.ok || !res.body) {
    const text = await res.text().catch(() => '');
    onError(text || `HTTP ${res.status}`);
    return false;
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = '';
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    let idx: number;
    while ((idx = buf.indexOf('\n\n')) !== -1) {
      const frame = buf.slice(0, idx);
      buf = buf.slice(idx + 2);
      let event = 'message';
      const dataLines: string[] = [];
      for (const line of frame.split('\n')) {
        if (line.startsWith('event:')) event = line.slice(6).trim();
        // SSE spec: ONE optional leading space after `data:` is part of
        // the prefix; everything else is payload. .trim() here would
        // nuke token-leading spaces (`" world"` → `"world"`) which
        // collapses streamed model output into one space-less blob.
        else if (line.startsWith('data:'))
          dataLines.push(line.startsWith('data: ') ? line.slice(6) : line.slice(5));
      }
      // Rejoin with '\n', per the spec. The Pod JSON-encodes payloads so
      // a frame normally has exactly one data line — but a Pod on an
      // older sessionenv image still emits raw multi-line strings, and
      // concatenating those without the separator is what silently ate
      // the newlines out of streamed markdown.
      const data = dataLines.join('\n');
      let payload = data;
      try { payload = JSON.parse(data); } catch { /* fall through */ }
      if (event === 'delta') onDelta(payload);
      else if (event === 'thinking') onThinking?.(payload);
      else if (event === 'tool_ui') {
        // Guarded: a Pod on a newer image than this SPA is exactly the
        // case the unknown-event contract exists for, and a malformed
        // payload must not kill the stream mid-turn.
        const frame = payload as { tool_call_id?: string; markdown?: string };
        if (frame && typeof frame.markdown === 'string') {
          onToolUi?.(frame.tool_call_id ?? '', frame.markdown);
        }
      }
      else if (event === 'tool_started') {
        const f = payload as { tool_call_id?: string; name?: string; input?: unknown };
        if (f && typeof f.name === 'string') {
          onToolStarted?.(f.tool_call_id ?? '', f.name, f.input);
        }
      }
      else if (event === 'tool_completed') {
        const f = payload as {
          tool_call_id?: string; output?: string; is_error?: boolean;
        };
        if (f) onToolCompleted?.(f.tool_call_id ?? '', f.output ?? '', f.is_error === true);
      }
      else if (event === 'error') onError(payload);
      else if (event === 'done') onDone();
    }
  }
  return true;
}
